import { z } from 'zod';
import { isoDateSchema } from './people.js';

/**
 * Relatórios e indicadores (M14).
 *
 * Três audiências, três perguntas diferentes:
 *
 * - **quem serve** pergunta "quando eu sirvo, e o que falta eu responder?";
 * - **quem lidera um departamento** pergunta "o próximo domingo está coberto?";
 * - **o pastor** pergunta "a igreja está sustentando quem serve, ou queimando?".
 *
 * Nenhum número aqui é enfeite: cada um responde a uma dessas três perguntas, e
 * o que não responde não entra na tela.
 */

// ── Período ──────────────────────────────────────────────────────────────────

export const periodoSchema = z.object({
  from: isoDateSchema,
  to: isoDateSchema,
  ministryId: z.string().uuid().nullish(),
  congregationId: z.string().uuid().nullish(),
});
export type PeriodoInput = z.infer<typeof periodoSchema>;

/** Atalhos que a igreja usa de verdade. */
export const ATALHOS_DE_PERIODO = [
  { chave: 'mes', rotulo: 'Este mês' },
  { chave: 'mes_passado', rotulo: 'Mês passado' },
  { chave: 'trimestre', rotulo: 'Últimos 3 meses' },
  { chave: 'ano', rotulo: 'Este ano' },
] as const;
export type AtalhoDePeriodo = (typeof ATALHOS_DE_PERIODO)[number]['chave'];

// ── Dashboard de quem serve ──────────────────────────────────────────────────

export const painelDoVoluntarioSchema = z.object({
  proxima: z
    .object({
      assignmentId: z.string().uuid(),
      eventTitle: z.string(),
      functionName: z.string(),
      startsAt: z.string().datetime(),
      localDate: z.string(),
      status: z.string(),
      precisaResponder: z.boolean(),
    })
    .nullable(),
  /** Quantas escalas eu tenho neste mês, e quantas já respondi. */
  noMes: z.number().int(),
  aguardandoMinhaResposta: z.number().int(),
  /** Quantas vezes servi nos últimos três meses. */
  noTrimestre: z.number().int(),
  bloqueiosAtivos: z.number().int(),
  /** Presença: de quantas escalas passadas eu apareci. */
  presencas: z.number().int(),
  faltas: z.number().int(),
});
export type PainelDoVoluntario = z.infer<typeof painelDoVoluntarioSchema>;

// ── Dashboard do líder de departamento ───────────────────────────────────────

export const vagaEmRiscoSchema = z.object({
  eventId: z.string().uuid(),
  eventTitle: z.string(),
  startsAt: z.string().datetime(),
  localDate: z.string(),
  functionName: z.string(),
  ministryName: z.string(),
  faltam: z.number().int(),
  essencial: z.boolean(),
});
export type VagaEmRisco = z.infer<typeof vagaEmRiscoSchema>;

export const cargaDaPessoaSchema = z.object({
  personId: z.string().uuid(),
  personName: z.string(),
  escalas: z.number().int(),
  confirmadas: z.number().int(),
  faltas: z.number().int(),
  /** Dias desde a última vez que serviu. Nulo se nunca serviu. */
  diasDesdeAUltima: z.number().int().nullable(),
});
export type CargaDaPessoa = z.infer<typeof cargaDaPessoaSchema>;

export const painelDoLiderSchema = z.object({
  /** Cobertura dos próximos eventos, em porcentagem inteira. */
  cobertura: z.number().int(),
  vagasAbertas: z.number().int(),
  vagasEssenciaisAbertas: z.number().int(),
  confirmacoesPendentes: z.number().int(),
  proximosEventos: z.number().int(),
  emRisco: z.array(vagaEmRiscoSchema),
  /** Quem está servindo demais, no topo. */
  carga: z.array(cargaDaPessoaSchema),
  /** Habilitado e sem escalar há muito tempo — ou nunca. */
  ociosos: z.array(cargaDaPessoaSchema),
  faltasRecentes: z.array(cargaDaPessoaSchema),
});
export type PainelDoLider = z.infer<typeof painelDoLiderSchema>;

// ── Dashboard do pastor ──────────────────────────────────────────────────────

export const departamentoNoRankingSchema = z.object({
  ministryId: z.string().uuid(),
  ministryName: z.string(),
  cobertura: z.number().int(),
  vagasAbertas: z.number().int(),
  pessoasAtivas: z.number().int(),
});
export type DepartamentoNoRanking = z.infer<typeof departamentoNoRankingSchema>;

export const painelExecutivoSchema = z.object({
  periodo: z.object({ from: z.string(), to: z.string() }),
  cobertura: z.number().int(),
  /** Das convocações enviadas, quantas foram respondidas com sim. */
  taxaDeConfirmacao: z.number().int(),
  /** De quem foi escalado, quantos apareceram. */
  taxaDePresenca: z.number().int(),
  servosAtivos: z.number().int(),
  novosServos: z.number().int(),
  /** Quem servia no período anterior e continuou servindo neste. */
  retencao: z.number().int(),
  /** Departamentos com mais buracos primeiro: é onde a liderança age. */
  ranking: z.array(departamentoNoRankingSchema),
  /** Quantas escalas foram publicadas no período. */
  escalasPublicadas: z.number().int(),
  criancasAtendidas: z.number().int(),
  /**
   * O período fatiado no tempo — por semana quando é curto, por mês quando
   * passa de dez semanas.
   *
   * `vagas` vem junto porque **muda o significado dos outros dois**: cobertura
   * de um pedaço sem vaga nenhuma é 100 por definição, e desenhar isso faria a
   * linha subir ao topo justamente na semana em que não houve culto.
   */
  /** De quanto em quanto tempo a série corta o período. A tela escreve isso. */
  serieAgrupadaPor: z.enum(['semana', 'mes']),
  serie: z.array(
    z.object({
      inicio: z.string(),
      rotulo: z.string(),
      cobertura: z.number().int(),
      presenca: z.number().int(),
      vagas: z.number().int(),
    }),
  ),
});
export type PainelExecutivo = z.infer<typeof painelExecutivoSchema>;

// ── Relatórios exportáveis ───────────────────────────────────────────────────

export const RELATORIOS = [
  'escala_do_mes',
  'frequencia_por_pessoa',
  'historico_individual',
  'ausencias',
  'uso_do_sistema',
  'frequencia_do_kids',
] as const;
export const relatorioSchema = z.enum(RELATORIOS);
export type Relatorio = z.infer<typeof relatorioSchema>;

export const RELATORIO_LABEL: Record<Relatorio, string> = {
  escala_do_mes: 'Escala do mês por departamento',
  frequencia_por_pessoa: 'Frequência por pessoa',
  historico_individual: 'Histórico individual',
  ausencias: 'Ausências',
  uso_do_sistema: 'Uso do sistema',
  frequencia_do_kids: 'Frequência do infantil',
};

export const RELATORIO_AJUDA: Record<Relatorio, string> = {
  escala_do_mes: 'Quem serve em cada culto do mês, por departamento e função.',
  frequencia_por_pessoa: 'Quantas vezes cada pessoa serviu, confirmou e apareceu.',
  historico_individual: 'Tudo o que uma pessoa serviu no período, culto a culto.',
  ausencias: 'Quem faltou, quando, e o que foi registrado.',
  uso_do_sistema: 'Quem entrou, quem respondeu pelo aplicativo, o que a igreja usa.',
  frequencia_do_kids: 'Quantas crianças por sala e por culto, e quem parou de vir.',
};

export const FORMATOS = ['pdf', 'xlsx'] as const;
export const formatoSchema = z.enum(FORMATOS);
export type FormatoDeExportacao = z.infer<typeof formatoSchema>;

export const exportRequestSchema = periodoSchema.extend({
  relatorio: relatorioSchema,
  formato: formatoSchema,
  /** Para o histórico individual. */
  personId: z.string().uuid().nullish(),
});
export type ExportRequestInput = z.infer<typeof exportRequestSchema>;

/** Uma tabela pronta para virar PDF, XLSX ou tela — a mesma, nas três. */
export const tabelaDoRelatorioSchema = z.object({
  titulo: z.string(),
  subtitulo: z.string(),
  colunas: z.array(z.object({ chave: z.string(), rotulo: z.string(), numerica: z.boolean() })),
  linhas: z.array(z.record(z.string(), z.union([z.string(), z.number()]))),
  /** Frase final: o total, a média, o que a liderança lê primeiro. */
  resumo: z.string().nullable(),
  geradoEm: z.string().datetime(),
});
export type TabelaDoRelatorio = z.infer<typeof tabelaDoRelatorioSchema>;

// ── Compartilhável ───────────────────────────────────────────────────────────

/**
 * A escala do culto pronta para o grupo do WhatsApp e para o mural (D22).
 *
 * O servidor entrega o **conteúdo**; a imagem 1080×1080 é desenhada no
 * navegador, que já tem as fontes e o canvas — e assim o produto não carrega um
 * conversor de imagem no servidor só para isso.
 */
export const escalaCompartilhavelSchema = z.object({
  eventId: z.string().uuid(),
  igreja: z.string(),
  titulo: z.string(),
  data: z.string(),
  hora: z.string(),
  local: z.string().nullable(),
  /** Cor da marca da igreja, para a arte sair com a cara dela. */
  cor: z.string(),
  equipes: z.array(
    z.object({
      ministerio: z.string(),
      pessoas: z.array(z.object({ funcao: z.string(), nome: z.string() })),
    }),
  ),
  /** Quantas vagas ainda estão abertas — a arte não mente sobre isso. */
  vagasAbertas: z.number().int(),
});
export type EscalaCompartilhavel = z.infer<typeof escalaCompartilhavelSchema>;
